import { useMemo } from "react";

import {
    DataExtractor,
    forEach,
    NodeDescriptor,
    DataView,
} from "@impero-com/common/utils/dataView";

import {
    RiskDirectoryBlessedFieldIds,
    MitigatingControl,
    RiskDirectoryNodeWithDetailsDto as RiskDirectoryNodeDto,
} from "api/generated/model";
import { RiskNode } from "org/risks/types";
import * as R from "org/risks/types";

import { DropdownInstance, ExpandedRiskNode, MapNode } from "../../types";
import { forwardToRisk } from "../../utils";

import {
    RiskInsightsTableRow,
    RiskInsightsRowStatus,
    RiskInsightsColumnNames,
} from "./RiskInsightsTable";

interface UseRiskInsightsDataResult {
    rows: RiskInsightsTableRow[];
    columnNames: RiskInsightsColumnNames;
}

export function useRiskInsightsTableData(
    props: UseRiskInsightsDataProps
): UseRiskInsightsDataResult {
    const {
        startDataView,
        startDateRootNode,
        startDateRiskDirectoryView,
        endDataView,
        endDateRootNode,
        endDateRiskDirectoryView,
        visibleNodeIds,
    } = props;

    return useMemo(
        () =>
            computeRiskInsightsTableData(
                startDataView,
                startDateRootNode,
                startDateRiskDirectoryView,
                endDataView,
                endDateRootNode,
                endDateRiskDirectoryView,
                visibleNodeIds
            ),
        [
            startDataView,
            startDateRootNode,
            startDateRiskDirectoryView,
            endDataView,
            endDateRootNode,
            endDateRiskDirectoryView,
            visibleNodeIds,
        ]
    );
}

export function computeRiskInsightsTableData(
    startDataView: DataView<NodeDescriptor<MapNode>>,
    startDateRootNode: RiskDirectoryNodeDto | undefined,
    startDateRiskDirectoryView: R.RiskDirectoryView | undefined,
    endDataView: DataView<NodeDescriptor<MapNode>>,
    endDateRootNode: RiskDirectoryNodeDto | undefined,
    endDateRiskDirectoryView: R.RiskDirectoryView | undefined,
    visibleNodeIds: Set<string>
): UseRiskInsightsDataResult {
    if (!endDateRootNode || !endDateRiskDirectoryView) {
        throw new Error(
            "RiskInsightsData requires endDateRootNode and endDateRiskDirectoryView"
        );
    }

    // Retrieve the relevant nodes: the visible expanded risk nodes.
    const endDateNodes: ExpandedRiskNodeDescriptor[] = [];
    forEach(endDataView.dataSource.iter(), (node) => {
        if (
            isExpandedRiskNodeDescriptor(node) &&
            visibleNodeIds.has(node.value.id)
        ) {
            endDateNodes.push(node);
        }
    });

    const startDateNodes: ExpandedRiskNodeDescriptor[] = [];
    forEach(startDataView.dataSource.iter(), (node) => {
        if (
            isExpandedRiskNodeDescriptor(node) &&
            visibleNodeIds.has(node.value.id)
        ) {
            startDateNodes.push(node);
        }
    });

    const startDateNodesMap = new Map<string, ExpandedRiskNodeDescriptor>();
    for (const node of startDateNodes) {
        startDateNodesMap.set(node.value.id, node);
    }

    const riskInsightNodes: {
        start?: ExpandedRiskNodeDescriptor;
        end?: ExpandedRiskNodeDescriptor;
    }[] = [];

    // First, add all end date nodes with their corresponding start date nodes
    for (const endNode of endDateNodes) {
        const startNode = startDateNodesMap.get(endNode.value.id);
        riskInsightNodes.push({
            start: startNode,
            end: endNode,
        });
        // Mark this node as processed
        if (startNode) {
            startDateNodesMap.delete(endNode.value.id);
        }
    }

    // Then, append any remaining start date nodes that don't exist in end date
    for (const startNode of startDateNodesMap.values()) {
        riskInsightNodes.push({
            start: startNode,
            end: undefined,
        });
    }

    const endBlessedExtractors = resolveBlessedExtractors(
        endDateRiskDirectoryView.content.resource.structure.blessedFieldIds,
        endDataView
    );
    const startBlessedExtractors = startDateRiskDirectoryView
        ? resolveBlessedExtractors(
              startDateRiskDirectoryView.content.resource.structure
                  .blessedFieldIds,
              startDataView
          )
        : {
              inherentImpact: undefined,
              inherentLikelihood: undefined,
              residualImpact: undefined,
              residualLikelihood: undefined,
          };

    // Build entity ID → name map from linked resources
    // Prefer values from endDateRootNode, as they are newer.
    const entityIdToName = new Map<number, string>();
    if (startDateRootNode) {
        for (const entity of startDateRootNode.linkedResources.entities) {
            entityIdToName.set(entity.id, entity.name);
        }
    }
    for (const entity of endDateRootNode.linkedResources.entities) {
        entityIdToName.set(entity.id, entity.name);
    }

    const rows = riskInsightNodes.map(({ start, end }) =>
        buildTableRow(
            start,
            end,
            startBlessedExtractors,
            endBlessedExtractors,
            entityIdToName
        )
    );

    if (
        !endBlessedExtractors.inherentImpact ||
        !endBlessedExtractors.inherentLikelihood ||
        !endBlessedExtractors.residualImpact ||
        !endBlessedExtractors.residualLikelihood
    ) {
        throw new Error(
            "RiskInsightsData requires all blessed fields to be present"
        );
    }

    const columnNames: RiskInsightsColumnNames = {
        inherentImpact: endBlessedExtractors.inherentImpact.label,
        inherentLikelihood: endBlessedExtractors.inherentLikelihood.label,
        residualImpact: endBlessedExtractors.residualImpact.label,
        residualLikelihood: endBlessedExtractors.residualLikelihood.label,
    };

    return { rows, columnNames };
}

export interface RiskWithProcess {
    risk: RiskNode;
    processName: string;
    processId: string | undefined;
    scope: "Global" | "Local";
    linkedEntityIds: number[];
}

/**
 * Converts a pair of optional ExpandedRiskNodeDescriptor (start/end) into a table row
 * by diffing their metadata values for each blessed field (impact, likelihood, coverage).
 */
function buildTableRow(
    start: ExpandedRiskNodeDescriptor | undefined,
    end: ExpandedRiskNodeDescriptor | undefined,
    startExtractors: ResolvedBlessedExtractors,
    endExtractors: ResolvedBlessedExtractors,
    entityIdToName: Map<number, string>
): RiskInsightsTableRow {
    if (!start && !end) {
        throw new Error("buildTableRow2 requires at least one of start or end");
    }

    const node = end ?? start!;
    const entryKey = node.value.id;
    const risk = node.value.risk;
    const entityId = node.value.entityId;

    let status: RiskInsightsRowStatus = null;
    if (!end) {
        status = "removed";
    } else if (!start) {
        status = "new";
    }

    const inherentImpact = extractMetadataChange(
        start,
        end,
        startExtractors.inherentImpact,
        endExtractors.inherentImpact
    );
    const inherentLikelihood = extractMetadataChange(
        start,
        end,
        startExtractors.inherentLikelihood,
        endExtractors.inherentLikelihood
    );
    const residualImpact = extractMetadataChange(
        start,
        end,
        startExtractors.residualImpact,
        endExtractors.residualImpact
    );
    const residualLikelihood = extractMetadataChange(
        start,
        end,
        startExtractors.residualLikelihood,
        endExtractors.residualLikelihood
    );

    const startCoverage = start?.value.content
        ? calculateAverageRiskCoverage(start.value.content.mitigatingControls)
        : null;

    const endCoverage = end?.value.content
        ? calculateAverageRiskCoverage(end.value.content.mitigatingControls)
        : null;

    let coverage: RiskInsightsTableRow["coverage"];
    if (startCoverage === null && endCoverage === null) {
        coverage = null;
    } else if (
        startCoverage !== null &&
        endCoverage !== null &&
        startCoverage !== endCoverage
    ) {
        coverage = {
            fromPercentage: startCoverage,
            toPercentage: endCoverage,
        };
    } else {
        coverage = {
            noChangePercentage: endCoverage ?? startCoverage ?? 0,
        };
    }

    return {
        id: entryKey,
        name: risk.content.name,
        status,
        process: node.ancestors
            .map(forwardToRisk((ancestor) => ancestor.content.name))
            .join(" / "),
        entity: entityIdToName.get(entityId) ?? "",
        inherentImpact,
        inherentLikelihood,
        residualImpact,
        residualLikelihood,
        coverage,
    };
}

interface ResolvedBlessedExtractors {
    inherentImpact: DropdownInstanceExtractor | undefined;
    inherentLikelihood: DropdownInstanceExtractor | undefined;
    residualImpact: DropdownInstanceExtractor | undefined;
    residualLikelihood: DropdownInstanceExtractor | undefined;
}

type ExpandedRiskNodeDescriptor = NodeDescriptor<MapNode> & {
    value: ExpandedRiskNode;
};
type DropdownInstanceExtractor = DataExtractor<
    ExpandedRiskNodeDescriptor,
    DropdownInstance
>;

function isExpandedRiskNodeDescriptor(
    node: NodeDescriptor<MapNode>
): node is ExpandedRiskNodeDescriptor {
    return node.value.type === "ExpandedRiskNode";
}

function resolveBlessedExtractors(
    blessedFieldIds: RiskDirectoryBlessedFieldIds,
    dataView: DataView<NodeDescriptor<MapNode>>
): ResolvedBlessedExtractors {
    function findExtractorById(
        id: string
    ): DropdownInstanceExtractor | undefined {
        return dataView.extractorGroups
            .flatMap((group) => group.extractors)
            .find((extractor) =>
                extractor.id.startsWith(`risk_metadata_entityLevel_${id}_`)
            );
    }

    return {
        inherentImpact: findExtractorById(blessedFieldIds.inherentImpactId),
        inherentLikelihood: findExtractorById(
            blessedFieldIds.inherentLikelihoodId
        ),
        residualImpact: findExtractorById(blessedFieldIds.residualImpactId),
        residualLikelihood: findExtractorById(
            blessedFieldIds.residualLikelihoodId
        ),
    };
}

function calculateAverageRiskCoverage(
    mitigation: MitigatingControl[]
): number | null {
    const coverages: number[] = mitigation
        .filter((control) => control.riskCoverage !== null)
        .map((control) => control.riskCoverage!);

    if (coverages.length === 0) {
        return null;
    }

    const sum = coverages.reduce((acc, val) => acc + val, 0);
    return Math.round(sum / coverages.length);
}

function getMetadataOptionIndex(
    metadataInstance: DropdownInstance | undefined
): number | undefined {
    const selectedId = metadataInstance?.value?.id;
    if (!selectedId) {
        return undefined;
    }

    const index = metadataInstance.fieldType.options.findIndex(
        (opt) => opt.id === selectedId
    );
    return index !== -1 ? index : undefined;
}

/**
 * Helper to extract change props for a metadata field (impact or likelihood)
 * Works for both inherent and residual fields.
 */
function extractMetadataChange(
    startNode: ExpandedRiskNodeDescriptor | undefined,
    endNode: ExpandedRiskNodeDescriptor | undefined,
    startExtractor: DropdownInstanceExtractor | undefined,
    endExtractor: DropdownInstanceExtractor | undefined
): RiskInsightsTableRow["inherentImpact"] {
    if (!startExtractor && !endExtractor) {
        return { noChangeLabel: "-" };
    }

    const startValue =
        startNode && startExtractor
            ? startExtractor.getValue(startNode)
            : undefined;
    const startLabel = startValue?.value?.value;
    const startIndex = getMetadataOptionIndex(startValue);

    const endValue =
        endNode && endExtractor ? endExtractor.getValue(endNode) : undefined;
    const endLabel = endValue?.value?.value;
    const endIndex = getMetadataOptionIndex(endValue);

    // New/removed risks just show their current value — no diff arrow.
    // A "new" risk has no startNode (didn't exist at the start date).
    // A "removed" risk has no endNode (doesn't exist at the end date).
    // This is distinct from an *existing* risk whose field was previously
    // empty — that case still shows "No value -> [value]".
    const isNewRisk = !startNode;
    const isRemovedRisk = !endNode;
    if (isNewRisk) {
        return {
            noChangeLabel: endLabel ?? "-",
            noChangeIndex: endIndex,
        };
    }
    if (isRemovedRisk) {
        return {
            noChangeLabel: startLabel ?? "-",
            noChangeIndex: startIndex,
        };
    }

    // Both dates have the risk — show a diff if the value changed.
    const hasChanged = startLabel !== endLabel;
    if (hasChanged) {
        return {
            fromLabel: startLabel,
            fromIndex: startIndex,
            toLabel: endLabel,
            toIndex: endIndex,
        };
    }

    return {
        noChangeLabel: endLabel ?? startLabel ?? "-",
        noChangeIndex: endIndex ?? startIndex,
    };
}

interface UseRiskInsightsDataProps {
    startDataView: DataView<NodeDescriptor<MapNode>>;
    startDateRootNode: RiskDirectoryNodeDto | undefined;
    startDateRiskDirectoryView: R.RiskDirectoryView | undefined;
    endDataView: DataView<NodeDescriptor<MapNode>>;
    endDateRootNode: RiskDirectoryNodeDto | undefined;
    endDateRiskDirectoryView: R.RiskDirectoryView | undefined;
    visibleNodeIds: Set<string>;
}
